
  import { defineComponent, toRefs, computed, ref, onMounted, watch } from 'vue'
  import moment from 'moment'
  import { Tag, Button } from '@up.law/uplaw-ui'
  import { formatNumber } from '@/utils'
  import { useStore } from 'vuex'
  import { isNumber } from 'lodash'
  import { CompanyModel, Event, NominalValue } from '@/services/api/models'
  import { EventType } from '@/types/event'
  import { useRoute, useRouter } from 'vue-router'
  import { Pagination } from '@/store/modules/Organization/models'
  import { ElPagination } from 'element-plus'
  import EclipseLoader from '@/components/Loader/eclipse.vue'
  import ContactBadge from '@/components/ContactBadge/index.vue'
  import { ElPopconfirm } from 'element-plus'

  export default defineComponent({
    name: 'IssuanceCard',
    components: {
      Tag,
      ContactBadge,
      ElPagination,
      EclipseLoader,
      ElPopconfirm,
      Button
    },
    props: {
      event: {
        type: Object as () => Event | null,
        required: true
      },
      nominalValue: {
        type: Number,
        default: 0
      },
      filters: {
        type: Object as () => any,
        default: () => ({})
      },
      isPortfolio: {
        type: Boolean,
        default: false
      }
    },
    setup(props) {
      const route = useRoute()
      const router = useRouter()
      const store = useStore()
      // const { nominalValue } = toRefs(props)
      const contactsId = computed<string[]>(
        () => (props.filters as any)?.contactsId || []
      )
      const companyId = computed<string | undefined>(
        () => props.event?.company?.id
      )
      const isLoadingParts = ref(false)

      const issuanceParts = ref<any[]>(
        props.event?.stockIssuance?.stockIssuanceParts ?? []
      )
      const limit = computed<number>(
        () => store.getters['config/eventPartsLimit']
      )
      const pagination = ref<Pagination>({
        total: 0,
        currentPage: 1,
        lastPage: 1,
        limit: limit.value
      })
      const company = computed<CompanyModel>(
        () => store.getters['organizations/contextCompany']
      )
      const nominalValues = computed<NominalValue[]>(
        () => store.getters['organizations/nominalValues']
      )
      const nominalValue = computed(() => {
        const sorted = [...nominalValues.value].sort(
          (a, b) => moment.utc(b.date).unix() - moment.utc(a.date).unix()
        )
        const [nv] = sorted.filter(
          (x) =>
            moment.utc(props.event?.date).unix() > moment.utc(x.date).unix()
        )
        return nv?.nominalValue
      })

      const isLoading = ref(false)
      const stock = computed<any>(() => {
        switch (props.event?.type?.toString()) {
          case 'OPTION_ISSUANCE':
            return props.event?.optionIssuance
          case 'COMPOSITION_ISSUANCE':
            return props.event?.compositionIssuance
          default:
            return props.event?.stockIssuance
        }
      })

      const isDetailPage = computed(() => route.name === 'company.eventDetails')

      const isOptionIssuance = computed(
        () => props.event?.type?.toString() === EventType.optionIssuance
      )

      const isStockIssuance = computed(
        () => props.event?.type?.toString() === EventType.stockIssuance
      )

      const isCompositionIssuance = computed(
        () => props.event?.type?.toString() === EventType.compositionIssuance
      )
      const canDisplayAction = computed(
        () =>
          isDetailPage.value &&
          (isStockIssuance.value || isOptionIssuance.value)
      )

      const currency = computed(() => store.getters['organizations/currency'])

      const parts = computed<any[]>(() => {
        switch (props.event?.type?.toString()) {
          case 'OPTION_ISSUANCE':
            return stock.value?.optionIssuanceParts
          case 'COMPOSITION_ISSUANCE':
            return stock.value?.compositionIssuanceParts || []
          default:
            return stock.value?.stockIssuanceParts || []
        }
      })

      const colsCount = computed(() => {
        let value = 8
        if (!canDisplayAction.value) {
          if (!isOptionIssuance.value) value = 9
        } else {
          if (!isCompositionIssuance.value && !isOptionIssuance.value)
            value = 10
          else value = 9
        }

        return value
      })

      const isOverLimit = computed(
        () =>
          !isDetailPage.value &&
          isStockIssuance.value &&
          parts.value.length > limit.value
      )

      const totalQuantity = computed(() =>
        (
          (isStockIssuance.value ? issuanceParts.value : parts.value) || []
        ).reduce((total: number, p: any) => (total += p.quantity), 0)
      )
      const globalReduction = computed(() =>
        (
          (isStockIssuance.value ? issuanceParts.value : parts.value) || []
        ).reduce(
          (acc: number, line: any) =>
            (acc += parseInt(line.quantity || 0) * nominalValue.value),
          0
        )
      )

      const globalReleased = computed(() =>
        (
          (isStockIssuance.value ? issuanceParts.value : parts.value) || []
        ).reduce(
          (acc: number, line: any) =>
            (acc += parseFloat(line.releasedAmount || 0)),
          0
        )
      )
      const globalEmit = computed(() =>
        (
          (isStockIssuance.value ? issuanceParts.value : parts.value) || []
        ).reduce(
          (acc: number, line: any) =>
            (acc +=
              (parseFloat(line.totalPrice) / parseInt(line.quantity || 0) -
                nominalValue.value) *
              parseInt(line.quantity || 0)),
          0
        )
      )

      const globalIssuancePerAction = computed(() =>
        (
          (isStockIssuance.value ? issuanceParts.value : parts.value) || []
        ).reduce(
          (acc: number, line: any) =>
            (acc +=
              parseInt(line.totalPrice) / parseInt(line.quantity || 0) -
              nominalValue.value),
          0
        )
      )
      const globalNominalIncrease = computed(() =>
        (
          (isStockIssuance.value ? issuanceParts.value : parts.value) || []
        ).reduce(
          (acc: number, line: any) =>
            (acc += parseInt(line.quantity || 0) * nominalValue.value),
          0
        )
      )
      const globalTotal = computed(() =>
        (
          (isStockIssuance.value ? issuanceParts.value : parts.value) || []
        ).reduce(
          (acc: number, line: any) => (acc += parseFloat(line.totalPrice || 0)),
          0
        )
      )

      const globalUnitPrice = computed(() => {
        let value = 0
        if (globalTotal.value > 0 && totalQuantity.value > 0) {
          value =
            parseFloat(globalTotal.value?.toString()) / totalQuantity.value
        }
        return value
      })

      const calculateUnitPrice = (row: any) => {
        if (!row.totalPrice || !row.quantity) return 0

        return row.totalPrice / row.quantity
      }

      const emitPerAction = (row: any) => {
        const unitPrice = calculateUnitPrice(row)

        if (!isNumber(unitPrice)) {
          return '-'
        }

        return Math.abs(unitPrice - nominalValue.value)
      }

      const emitTotal = (row: any) => {
        const ePerAction: number | string = emitPerAction(row)
        if (ePerAction === '-') {
          return '-'
        }

        return parseFloat(ePerAction.toString()) * row.quantity
      }

      const augNominal = (row: any) => {
        if (nominalValue.value < 0) {
          return '-'
        }
        return nominalValue.value * row.quantity
      }

      const handlePageSelection = (page: number) => {
        getSTockIssuanceParts(props.event?.stockIssuance?.id, page)
      }

      const removePart = (partId: string) => {
        let id = props.event?.optionIssuance?.id
        let action = 'REMOVE_OPTION_ISSUANCE_PART'
        if (isStockIssuance.value) {
          id = props.event?.stockIssuance?.id
          action = 'REMOVE_STOCK_ISSUANCE_PART'
        }
        store.dispatch(`organizations/${action}`, {
          companyId: company.value?.id,
          stockIssuanceId: id,
          partId,
          onSuccess: () => {
            if (
              pagination.value.lastPage === 1 &&
              (parts.value.length === 1 || issuanceParts.value.length === 1)
            ) {
              router.go(-1)
            } else handlePageSelection(pagination.value.currentPage || 1)
          },
          onError: () => {
            //Oooops
          }
        })
      }

      const getSTockIssuanceParts = (id: string | undefined, page = 1) => {
        if (!id) return

        isLoadingParts.value = true
        store.dispatch('organizations/GET_STOCK_ISSUANCE_PARTS', {
          companyId: company.value?.id,
          stockIssuanceId: id,
          page,
          limit: isDetailPage.value ? 500 : null,
          onSuccess: (response: any, pageLimit = 0) => {
            const { data, metadata } = response
            pagination.value = {
              ...metadata,
              total: metadata?.count,
              limit: pageLimit
            }
            issuanceParts.value = [...data]
            isLoadingParts.value = false
          },
          onError: () => {
            isLoadingParts.value = false
          }
        })
      }

      watch(
        () => props.event,
        () => {
          getSTockIssuanceParts(props.event?.stockIssuance?.id)
        }
      )

      onMounted(() => {
        getSTockIssuanceParts(props.event?.stockIssuance?.id)
      })

      return {
        currency,
        parts,
        stock,
        limit,
        moment,
        globalReduction,
        totalQuantity,
        globalReleased,
        globalUnitPrice,
        globalNominalIncrease,
        globalIssuancePerAction,
        globalEmit,
        globalTotal,
        isLoading,
        isOptionIssuance,
        contactsId,
        issuanceParts,
        isStockIssuance,
        isOverLimit,
        pagination,
        isDetailPage,
        isLoadingParts,
        canDisplayAction,
        colsCount,
        companyId,
        isCompositionIssuance,
        formatNumber,
        calculateUnitPrice,
        emitPerAction,
        emitTotal,
        handlePageSelection,
        augNominal,
        removePart
      }
    }
  })
